"use client";

import { useState, useEffect } from "react";
import { UserX, User } from "lucide-react";
import { errorToast, successToast } from "../../utils/toasters";
import { removeReport } from "../../services/reportuser";

function ManageBlockedUsers({ blockedUsers: initialBlockedUsers = [],setIsProcessing }) {
  const [localBlockedUsers, setLocalBlockedUsers] = useState(
    initialBlockedUsers
  );
  const [isUnblockConfirmOpen, setIsUnblockConfirmOpen] = useState(false);
  const [userToUnblock, setUserToUnblock] = useState(null);

  useEffect(
    () => {
      setLocalBlockedUsers(initialBlockedUsers);
    },
    [initialBlockedUsers]
  );

  const handleUnblockUser = async userId => {
    try {
      setIsProcessing(true)
      console.log("userid", userId);
      await removeReport({ reported_user: userId });
      setLocalBlockedUsers(prevUsers =>
        prevUsers.filter(user => user.userid !== userId)
      );

      successToast("User Report Removed Successfully.");
    } catch (error) {
      console.error("Error unblocking user:", error);
      errorToast("Error while unblocking this user. Please try this again.");
    } finally {
      setIsProcessing(false)
      setIsUnblockConfirmOpen(false);
      setUserToUnblock(null);
    }
  };

  const handleUnblockClick = user => {
    setUserToUnblock(user);
    setIsUnblockConfirmOpen(true);
  };

  const handleCancelUnblock = () => {
    setIsUnblockConfirmOpen(false);
    setUserToUnblock(null);
  };

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-6">
      <h1 className="text-2xl font-bold mb-6">Blocked Users</h1>

      <div className="w-full">
        {/* Show empty state if no blocked users */}
        {!localBlockedUsers || localBlockedUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserX className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-lg font-medium mb-2">No blocked users</p>
            <p className="text-sm">You haven't blocked any users yet.</p>
          </div>
        ) : (
          <>
            {/* Header row */}
            <div className="grid grid-cols-2 bg-gray-100 p-3 rounded-t-md">
              <div className="text-sm text-gray-600">User</div>
              <div className="text-sm text-gray-600 text-right">Actions</div>
            </div>

            {/* User rows */}
            {localBlockedUsers.map(user => (
              <div
                key={user.id}
                className="grid grid-cols-2 py-4 border-b border-gray-100 items-center last:border-b-0"
              >
                {/* User column */}
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full overflow-hidden mr-3 bg-gray-100">
                    {user.avatarurl ? (
                      <img
                        src={user.avatarurl || "/placeholder.svg"}
                        alt={user.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.username}</p>
                    {user.firstname &&
                      user.lastname && (
                        <p className="text-sm text-gray-500">
                          {user.firstname} {user.lastname}
                        </p>
                      )}
                  </div>
                </div>

                {/* Actions column */}
                <div className="flex justify-end">
                  <button
                    onClick={() => handleUnblockClick(user)}
                    className="px-4 py-2 text-sm font-medium text-[#7B189F] bg-white border border-[#7B189F] rounded-md hover:bg-[#7B189F] hover:text-white transition-colors duration-200"
                  >
                    Unblock
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Unblock Confirmation Dialog */}
      {isUnblockConfirmOpen &&
        userToUnblock && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleCancelUnblock}
              />

              {/* Dialog */}
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                    <UserX className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Unblock User
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Are you sure you want to unblock{" "}
                    <span className="font-medium">
                      {userToUnblock.username}
                    </span>? They will be able to interact with you again.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={handleCancelUnblock}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleUnblockUser(userToUnblock.userid)}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#7B189F] border border-transparent rounded-md hover:bg-[#6A1587] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B189F]"
                    >
                      Yes, Unblock
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

export default ManageBlockedUsers;
